//src/pages/inspection/InspectionRequest.jsx
import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Row,
  Spinner,
} from "react-bootstrap";
import {
  MdAccessTime,
  MdArrowBack,
  MdCalendarMonth,
  MdHomeWork,
  MdLocationOn,
  MdOutlineNoteAlt,
  MdOutlineSchedule,
  MdVerified,
} from "react-icons/md";
import { Link, useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../axios";
import "../../styles/InspectionRequest.css";
import { formatRupiah } from "../../utils/currency";

const INSPECTION_TYPES = [
  {
    value: "property_inspection",
    label: "Inspeksi Properti",
    description: "Pemeriksaan umum kondisi properti secara menyeluruh.",
  },
  {
    value: "listing_verification",
    label: "Verifikasi Listing",
    description: "Pengecekan kesesuaian data iklan dengan kondisi di lapangan.",
  },
  {
    value: "valuation_support",
    label: "Bantuan Penilaian",
    description: "Inspeksi untuk membantu estimasi kondisi dan kelayakan properti.",
  },
];

const initialForm = {
  inspection_type: "property_inspection",
  preferred_date: "",
  preferred_time_start: "",
  preferred_time_end: "",
  owner_notes: "",
};

const formatDateLabel = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat("id-ID", {
    dateStyle: "full",
  }).format(date);
};

const InspectionRequest = () => {
  const { propertyId } = useParams();
  const navigate = useNavigate();

  const [property, setProperty] = useState(null);
  const [loadingProperty, setLoadingProperty] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [form, setForm] = useState(initialForm);
  const [successData, setSuccessData] = useState(null);

  const requestIdentifier = useMemo(() => {
    return property?.slug || propertyId;
  }, [property?.slug, propertyId]);

  const propertyLocation = useMemo(() => {
    if (!property) return "-";

    return (
      property.location ||
      [property.village, property.district, property.city, property.province]
        .filter(Boolean)
        .join(", ") ||
      "-"
    );
  }, [property]);

  const propertyImage =
    property?.image_url ||
    property?.main_image_url ||
    property?.images?.[0]?.display_image_url ||
    property?.images?.[0]?.image_url ||
    null;

  const selectedType = useMemo(
    () =>
      INSPECTION_TYPES.find((item) => item.value === form.inspection_type) ||
      INSPECTION_TYPES[0],
    [form.inspection_type],
  );

  const fetchProperty = async () => {
    if (!propertyId) return;

    setLoadingProperty(true);
    setError("");

    try {
      const response = await axiosInstance.get(`/properties/${propertyId}`);
      if (response.data?.success) {
        setProperty(response.data.data || null);
      } else {
        setError(response.data?.message || "Property tidak ditemukan.");
      }
    } catch (err) {
      setError(
        err?.response?.data?.message || "Property tidak ditemukan atau gagal dimuat.",
      );
    } finally {
      setLoadingProperty(false);
    }
  };

  useEffect(() => {
    fetchProperty();
  }, [propertyId]);

  const setFieldValue = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
    setError("");
  };

  const validateForm = () => {
    const nextErrors = {};

    if (!form.inspection_type) {
      nextErrors.inspection_type = "Jenis inspeksi wajib dipilih.";
    }

    if (!form.preferred_date) {
      nextErrors.preferred_date = "Tanggal preferensi wajib diisi.";
    } else {
      const selectedDate = new Date(`${form.preferred_date}T00:00:00`);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        nextErrors.preferred_date = "Tanggal preferensi tidak boleh di masa lalu.";
      }
    }

    if (!form.preferred_time_start) {
      nextErrors.preferred_time_start = "Jam mulai preferensi wajib diisi.";
    }

    if (
      form.preferred_time_start &&
      form.preferred_time_end &&
      form.preferred_date
    ) {
      const start = new Date(
        `${form.preferred_date}T${form.preferred_time_start}:00`,
      );
      const end = new Date(`${form.preferred_date}T${form.preferred_time_end}:00`);

      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
        nextErrors.preferred_time_end = "Format jam tidak valid.";
      } else if (end <= start) {
        nextErrors.preferred_time_end =
          "Jam selesai harus lebih besar dari jam mulai.";
      }
    }

    if (form.owner_notes && form.owner_notes.length > 2000) {
      nextErrors.owner_notes = "Catatan tidak boleh lebih dari 2000 karakter.";
    }

    setFormErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const buildScheduledAt = () => {
    if (!form.preferred_date || !form.preferred_time_start) return null;
    return `${form.preferred_date} ${form.preferred_time_start}:00`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!property) return;
    if (!validateForm()) return;

    setSubmitting(true);
    setError("");

    try {
      const payload = {
        inspection_type: form.inspection_type,
        preferred_date: form.preferred_date,
        preferred_time_start: form.preferred_time_start,
        preferred_time_end: form.preferred_time_end || null,
        scheduled_at: buildScheduledAt(),
        owner_notes: form.owner_notes || null,
      };

      const response = await axiosInstance.post(
        `/properties/${requestIdentifier}/inspection-requests`,
        payload,
      );

      if (response.data?.success) {
        setSuccessData(response.data.data || null);
      } else {
        setError(response.data?.message || "Gagal mengajukan inspeksi.");
      }
    } catch (err) {
      const apiErrors = err?.response?.data?.errors || null;

      if (apiErrors) {
        const mapped = {};
        Object.entries(apiErrors).forEach(([key, msgs]) => {
          mapped[key] = Array.isArray(msgs) ? msgs[0] : msgs;
        });
        setFormErrors(mapped);
      }

      setError(
        err?.response?.data?.message ||
        "Gagal mengajukan inspeksi. Silakan coba lagi.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingProperty) {
    return (
      <div className="inspection-request-page">
        <div className="inspection-request-hero">
          <div className="inspection-request-hero__content">
            <div className="inspection-skeleton inspection-skeleton__line inspection-skeleton__line--short" />
            <div className="inspection-skeleton inspection-skeleton__line inspection-skeleton__line--title" />
            <div className="inspection-skeleton inspection-skeleton__line" />
          </div>
        </div>

        <Card className="inspection-card">
          <Card.Body className="inspection-card__body">
            <div className="inspection-skeleton inspection-skeleton__block" />
            <div className="inspection-skeleton inspection-skeleton__block mt-3" />
            <div className="inspection-skeleton inspection-skeleton__block mt-3" />
          </Card.Body>
        </Card>
      </div>
    );
  }

  if (error && !property) {
    return (
      <div className="inspection-request-page">
        <Alert variant="danger" className="inspection-alert">
          <div className="d-flex justify-content-between align-items-center gap-3 flex-wrap">
            <span>{error}</span>
            <Button as={Link} to="/dashboard/properties" variant="light">
              Kembali
            </Button>
          </div>
        </Alert>
      </div>
    );
  }

  if (successData) {
    return (
      <div className="inspection-request-page">
        <div className="inspection-success-card">
          <div className="inspection-success-icon">
            <MdVerified size={36} />
          </div>

          <h1 className="inspection-success-title">
            Pengajuan Inspeksi Berhasil
          </h1>

          <p className="inspection-success-desc">
            Permintaan inspeksi Anda sudah dikirim dan akan diproses oleh tim kami.
          </p>

          <div className="inspection-success-meta">
            <span className="inspection-success-chip">
              Kode Request: <strong>{successData.request_code || "-"}</strong>
            </span>
            <span className="inspection-success-chip">
              Status: <strong>{successData.status || "-"}</strong>
            </span>
          </div>

          <div className="inspection-success-actions">
            <Button
              as={Link}
              to={`/dashboard/properties/${propertyId}`}
              variant="light"
              className="inspection-btn inspection-btn--ghost"
            >
              Lihat Properti
            </Button>

            <Button
              as={Link}
              to="/dashboard/properties"
              className="inspection-btn inspection-btn--primary"
            >
              Kembali ke Daftar Properti
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="inspection-request-page">
      <section className="inspection-request-hero">
        <div className="inspection-request-hero__content">
          <div className="inspection-request-hero__eyebrow">
            <MdOutlineSchedule size={18} />
            <span>Request Inspeksi Properti</span>
          </div>

          <h1 className="inspection-request-hero__title">
            Ajukan inspeksi untuk properti Anda
          </h1>

          <p className="inspection-request-hero__subtitle">
            Isi jadwal preferensi Anda, lalu tim kami akan memproses permintaan
            inspeksi sesuai jadwal preferensi yang Anda buat.
          </p>

          <div className="inspection-request-hero__chips">
            <Badge bg="light" text="dark" className="inspection-request-chip">
              {property?.property_type?.name || "Tipe Properti"}
            </Badge>
            <Badge bg="light" text="dark" className="inspection-request-chip">
              {property?.certificate_type?.name || "Sertifikat"}
            </Badge>
            <Badge bg="light" text="dark" className="inspection-request-chip">
              {property?.approval_status || "approval"}
            </Badge>
          </div>
        </div>

        <Button
          as={Link}
          to={`/dashboard/properties/${propertyId}`}
          variant="light"
          className="inspection-hero-back"
        >
          <MdArrowBack size={18} />
          Kembali ke Properti
        </Button>
      </section>

      {error ? (
        <Alert variant="danger" className="inspection-alert">
          {error}
        </Alert>
      ) : null}

      <div className="inspection-request-grid">
        <Card className="inspection-card inspection-form-card">
          <Card.Body className="inspection-card__body">
            <form onSubmit={handleSubmit}>
              <div className="inspection-section-header">
                <h2 className="inspection-section-title">
                  <MdOutlineSchedule />
                  Formulir Pengajuan
                </h2>
                <p className="inspection-section-desc">
                  Lengkapi data berikut untuk mengajukan inspeksi.
                </p>
              </div>

              <div className="inspection-form-group">
                <label className="inspection-label">
                  Jenis Inspeksi <span className="inspection-required">*</span>
                </label>

                <div className="inspection-type-grid">
                  {INSPECTION_TYPES.map((item) => {
                    const active = form.inspection_type === item.value;

                    return (
                      <button
                        key={item.value}
                        type="button"
                        className={`inspection-type-card ${active ? "is-active" : ""}`}
                        onClick={() => setFieldValue("inspection_type", item.value)}
                      >
                        <div className="inspection-type-card__head">
                          <span className="inspection-type-card__title">
                            {item.label}
                          </span>
                          {active ? (
                            <span className="inspection-type-card__active">
                              Dipilih
                            </span>
                          ) : null}
                        </div>

                        <div className="inspection-type-card__desc">
                          {item.description}
                        </div>
                      </button>
                    );
                  })}
                </div>

                {formErrors.inspection_type ? (
                  <div className="inspection-error">{formErrors.inspection_type}</div>
                ) : null}
              </div>

              <Row className="g-3">
                <Col md={6}>
                  <div className="inspection-form-group">
                    <label className="inspection-label">
                      Tanggal Preferensi{" "}
                      <span className="inspection-required">*</span>
                    </label>
                    <div className="inspection-input-wrap">
                      <MdCalendarMonth />
                      <input
                        type="date"
                        className="inspection-input"
                        value={form.preferred_date}
                        onChange={(e) =>
                          setFieldValue("preferred_date", e.target.value)
                        }
                        min={new Date().toISOString().slice(0, 10)}
                      />
                    </div>
                    {formErrors.preferred_date ? (
                      <div className="inspection-error">
                        {formErrors.preferred_date}
                      </div>
                    ) : null}
                  </div>
                </Col>

                <Col md={6}>
                  <div className="inspection-form-group">
                    <label className="inspection-label">
                      Jam Mulai <span className="inspection-required">*</span>
                    </label>
                    <div className="inspection-input-wrap">
                      <MdAccessTime />
                      <input
                        type="time"
                        className="inspection-input"
                        value={form.preferred_time_start}
                        onChange={(e) =>
                          setFieldValue("preferred_time_start", e.target.value)
                        }
                      />
                    </div>
                    {formErrors.preferred_time_start ? (
                      <div className="inspection-error">
                        {formErrors.preferred_time_start}
                      </div>
                    ) : null}
                  </div>
                </Col>

                <Col md={6}>
                  <div className="inspection-form-group">
                    <label className="inspection-label">Jam Selesai</label>
                    <div className="inspection-input-wrap">
                      <MdAccessTime />
                      <input
                        type="time"
                        className="inspection-input"
                        value={form.preferred_time_end}
                        onChange={(e) =>
                          setFieldValue("preferred_time_end", e.target.value)
                        }
                      />
                    </div>
                    {formErrors.preferred_time_end ? (
                      <div className="inspection-error">
                        {formErrors.preferred_time_end}
                      </div>
                    ) : null}
                  </div>
                </Col>

                <Col md={6}>
                  <div className="inspection-form-group">
                    <label className="inspection-label">Ringkasan Jadwal</label>
                    <div className="inspection-schedule-preview">
                      {form.preferred_date && form.preferred_time_start ? (
                        <>
                          {formatDateLabel(form.preferred_date)} •{" "}
                          {form.preferred_time_start}
                          {form.preferred_time_end
                            ? ` - ${form.preferred_time_end}`
                            : ""}
                        </>
                      ) : (
                        "Tanggal dan jam belum dipilih"
                      )}
                    </div>
                  </div>
                </Col>
              </Row>

              <div className="inspection-form-group">
                <label className="inspection-label">
                  Catatan Pemilik / Kebutuhan Khusus
                </label>
                <div className="inspection-input-wrap inspection-input-wrap--textarea">
                  <MdOutlineNoteAlt />
                  <textarea
                    className="inspection-textarea"
                    rows={5}
                    value={form.owner_notes}
                    onChange={(e) => setFieldValue("owner_notes", e.target.value)}
                    placeholder="Contoh: Mohon inspeksi dilakukan pada pagi hari. Akses lokasi melalui gerbang belakang."
                  />
                </div>
                <div className="inspection-helper">
                  Tambahkan informasi akses, waktu terbaik, atau catatan penting
                  lainnya.
                </div>
                {formErrors.owner_notes ? (
                  <div className="inspection-error">{formErrors.owner_notes}</div>
                ) : null}
              </div>

              <div className="inspection-form-actions">
                <Button
                  type="submit"
                  className="inspection-btn inspection-btn--primary inspection-btn--submit"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Spinner size="sm" animation="border" />
                      Mengirim...
                    </>
                  ) : (
                    <>
                      <MdOutlineSchedule size={18} />
                      Kirim Pengajuan Inspeksi
                    </>
                  )}
                </Button>
              </div>
            </form>
          </Card.Body>
        </Card>

        <aside className="inspection-sidebar">
          <Card className="inspection-card inspection-summary-card">
            <Card.Body className="inspection-card__body">
              <div className="inspection-summary-media">
                {propertyImage ? (
                  <img src={propertyImage} alt={property?.title || "Property"} />
                ) : (
                  <div className="inspection-summary-media__placeholder">
                    <MdHomeWork size={32} />
                    <span>Tidak ada gambar</span>
                  </div>
                )}
              </div>

              <div className="inspection-summary-head">
                <Badge bg="light" text="dark" className="inspection-request-chip">
                  {property?.property_type?.name || "-"}
                </Badge>
                <Badge bg="light" text="dark" className="inspection-request-chip">
                  {property?.certificate_type?.name || "-"}
                </Badge>
              </div>

              <h3 className="inspection-summary-title">
                {property?.title || "-"}
              </h3>

              <div className="inspection-summary-location">
                <MdLocationOn />
                <span>{propertyLocation}</span>
              </div>

              <div className="inspection-summary-list">
                <div className="inspection-summary-row">
                  <span>Harga</span>
                  <strong>{formatRupiah(property?.price)}</strong>
                </div>
                <div className="inspection-summary-row">
                  <span>Listing Source</span>
                  <strong>{property?.listing_source || "-"}</strong>
                </div>
                <div className="inspection-summary-row">
                  <span>Status Approval</span>
                  <strong>{property?.approval_status || "-"}</strong>
                </div>
                <div className="inspection-summary-row">
                  <span>Pemilik</span>
                  <strong>{property?.owner?.name || "-"}</strong>
                </div>
              </div>

              <div className="inspection-summary-note">
                <MdVerified size={18} />
                <span>
                  Template inspeksi dipilih otomatis berdasarkan tipe properti.
                </span>
              </div>
            </Card.Body>
          </Card>

          <Card className="inspection-card inspection-timeline-card">
            <Card.Body className="inspection-card__body">
              <div className="inspection-section-header">
                <h2 className="inspection-section-title">
                  <MdOutlineSchedule />
                  Alur Pengajuan
                </h2>
              </div>

              <div className="inspection-timeline">
                <div className="inspection-timeline__item">
                  <span className="inspection-timeline__dot" />
                  <div>
                    <strong>1. Kirim request</strong>
                    <p>Pengajuan masuk ke sistem dan menunggu proses.</p>
                  </div>
                </div>

                <div className="inspection-timeline__item">
                  <span className="inspection-timeline__dot" />
                  <div>
                    <strong>2. Peninjauan tim</strong>
                    <p>Tim akan memverifikasi jadwal dan kebutuhan inspeksi.</p>
                  </div>
                </div>

                <div className="inspection-timeline__item">
                  <span className="inspection-timeline__dot" />
                  <div>
                    <strong>3. Konfirmasi lanjutan</strong>
                    <p>Anda akan dihubungi untuk penjadwalan akhir.</p>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </aside>
      </div>
    </div>
  );
};

export default InspectionRequest;
